import type {
  Product, ProductCategory, Order, ShippingAddress, SavedAddress, User, AdminOrder, ProductInput,
  CustomerSummary, CustomerDetail, ProductBuyer, StoreSettings,
  SubscriptionState, SubscriberSummary, FeedbackListPage,
} from '../types';
import { getClientId, getSessionId } from '../lib/session';

const BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001/api';

export const API_ORIGIN = BASE.replace(/\/api\/?$/, '');

// Error thrown by `request` — carries optional structured fields the backend may
// return (a CSV row-error list, an `errorId` correlating to server logs, or the
// seconds until a rate-limited endpoint accepts requests again).
export interface ApiError extends Error {
  rowErrors?: { row: number; message: string }[];
  errorId?: string;
  status?: number;
  retryAfterSeconds?: number;
}

// Seconds until a 429'd endpoint opens up again, from whichever rate-limit
// header the server sent (Retry-After, draft-6 RateLimit-Reset, or the
// combined draft-7 RateLimit header). Exposed to CORS by the backend.
function parseRetryAfterSeconds(res: Response): number | undefined {
  for (const header of ['Retry-After', 'RateLimit-Reset']) {
    const value = Number(res.headers.get(header));
    if (Number.isFinite(value) && value > 0) return Math.ceil(value);
  }
  const combined = res.headers.get('RateLimit')?.match(/reset\s*=\s*(\d+)/i);
  return combined ? Number(combined[1]) : undefined;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      // Anonymous visitor/session ids on every request, so the backend can
      // link feedback (and future telemetry) to the same visitor regardless
      // of login state.
      'X-Client-Id': getClientId(),
      'X-Session-Id': getSessionId(),
      ...init?.headers,
    },
    ...init,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `Request failed: ${res.status}`) as ApiError;
    if (body.rowErrors) err.rowErrors = body.rowErrors;
    if (body.errorId) err.errorId = body.errorId;
    err.status = res.status;
    if (res.status === 429) err.retryAfterSeconds = parseRetryAfterSeconds(res);
    throw err;
  }
  return res.json();
}

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

export const productService = {
  getAll: () => request<Product[]>('/products'),
  getById: (id: string) => request<Product>(`/products/${id}`),
  getByCategory: (category: string) => request<Product[]>(`/products?category=${category}`),
  getFeatured: () => request<Product[]>('/products/featured'),
  // Categories that currently have at least one active product.
  getCategories: () => request<ProductCategory[]>('/products/categories'),
};

export interface CheckoutRequest {
  items: { productId: string; quantity: number }[];
  customerEmail: string;
  customerName: string;
  shipping: ShippingAddress;
  createAccount?: boolean;
  password?: string;
  subscribeNewsletter?: boolean;
  paymentToken?: string;
}

export interface CheckoutResponse {
  order: Order;
  paymentId: string;
  paymentStatus: string;
  // Per-order capability token that lets a guest view/confirm this order
  // without an account. Unrelated to the Square API secret.
  guestAccessToken: string;
  token?: string;
}

export interface PaymentConfig {
  provider: 'square';
  enabled: boolean;
  applicationId: string;
  locationId: string;
  environment: 'sandbox' | 'production';
}

export const orderService = {
  getPaymentConfig: () => request<PaymentConfig>('/orders/payment-config'),

  // Public storefront pricing (shipping + tax) so the cart/checkout match the server.
  getPricingConfig: () => request<StoreSettings>('/orders/pricing-config'),

  checkout: (data: CheckoutRequest, token?: string) =>
    request<CheckoutResponse>('/orders/checkout', {
      method: 'POST',
      body: JSON.stringify(data),
      ...(token ? { headers: { ...authHeaders(token), 'Content-Type': 'application/json' } } : {}),
    }),

  getById: (orderId: string, guestAccessToken?: string) =>
    request<Order>(`/orders/${orderId}${guestAccessToken ? `?guestAccessToken=${encodeURIComponent(guestAccessToken)}` : ''}`),

  /**
   * Read-after-write guard: polls until the order is actually readable, then
   * returns it. Tolerates a DB that isn't immediately read-your-writes
   * consistent (e.g. a future read-replica setup) by retrying on `404` (not
   * visible yet) and transient 5xx/network errors with exponential backoff.
   * Resolves with the order once visible, or `null` if it never appears within
   * the retry budget (callers can then degrade gracefully rather than hang).
   */
  waitForOrderReadable: async (
    orderId: string,
    guestAccessToken?: string,
    opts: { attempts?: number; baseDelayMs?: number } = {},
  ): Promise<Order | null> => {
    const attempts = opts.attempts ?? 6;
    const baseDelayMs = opts.baseDelayMs ?? 150;
    for (let attempt = 0; attempt < attempts; attempt++) {
      try {
        return await orderService.getById(orderId, guestAccessToken);
      } catch (err) {
        const status = (err as ApiError).status;
        const retryable = status === undefined || status === 404 || status >= 500;
        if (!retryable) throw err; // a real client error (e.g. 403) — don't spin
        if (attempt === attempts - 1) return null; // budget exhausted
        await sleep(baseDelayMs * 2 ** attempt);
      }
    }
    return null;
  },

  confirm: (orderId: string, guestAccessToken?: string) =>
    request<{ message: string; order: Order }>(
      `/orders/${orderId}/confirm${guestAccessToken ? `?guestAccessToken=${encodeURIComponent(guestAccessToken)}` : ''}`,
      { method: 'POST' },
    ),

  getTracking: (orderId: string, guestAccessToken?: string) =>
    request<{ orderId: string; status: string; tracking: { number: string; carrier: string } | null; estimatedDelivery: string | null }>(
      `/orders/${orderId}/tracking${guestAccessToken ? `?guestAccessToken=${encodeURIComponent(guestAccessToken)}` : ''}`,
    ),

  getHistory: (token: string) =>
    request<Order[]>('/orders/history/me', { headers: authHeaders(token) }),

  fulfill: (orderId: string, token: string, data: { trackingNumber: string; carrier: string }) =>
    request<{ message: string; order: Order }>(`/orders/${orderId}/fulfill`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    }),

  // amountCentsUSD is integer cents (e.g. 1000 = $10.00); omit for a full refund.
  refund: (orderId: string, token: string, data?: { amountCentsUSD?: number; reason?: string }) =>
    request<{ message: string; refundId: string; refundedCentsUSD: number; order: Order }>(
      `/orders/${orderId}/refund`,
      {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      },
    ),

  // Admin: re-pull payment/refund status from Square and reconcile this order.
  syncWithSquare: (orderId: string, token: string) =>
    request<{ message: string; changed: boolean; refundedCentsUSD: number; order: AdminOrder }>(
      `/orders/${orderId}/sync`,
      { method: 'POST', headers: authHeaders(token) },
    ),
};

export const authApi = {
  register: (email: string, password: string, name: string) =>
    request<{ user: User; token: string }>(
      '/auth/register',
      { method: 'POST', body: JSON.stringify({ email, password, name }) },
    ),

  login: (email: string, password: string) =>
    request<{ user: User; token: string }>(
      '/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
    ),

  forgotPassword: (email: string) =>
    request<{ message: string }>(
      '/auth/forgot-password',
      { method: 'POST', body: JSON.stringify({ email }) },
    ),

  resetPassword: (token: string, password: string) =>
    request<{ message: string }>(
      '/auth/reset-password',
      { method: 'POST', body: JSON.stringify({ token, password }) },
    ),

  me: (token: string) =>
    request<User>('/auth/me', { headers: authHeaders(token) }),

  getAddresses: (token: string) =>
    request<SavedAddress[]>('/auth/addresses', { headers: authHeaders(token) }),

  saveAddress: (token: string, address: ShippingAddress & { isDefault?: boolean }) =>
    request<SavedAddress>('/auth/addresses', {
      method: 'POST',
      body: JSON.stringify(address),
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    }),

  deleteAddress: (token: string, addressId: string) =>
    request<{ message: string }>(`/auth/addresses/${addressId}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    }),

  getSubscription: (token: string) =>
    request<SubscriptionState>('/auth/subscription', { headers: authHeaders(token) }),

  updateSubscription: (token: string, subscribe: boolean) =>
    request<SubscriptionState>('/auth/subscription', {
      method: 'PUT',
      body: JSON.stringify({ subscribe }),
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    }),
};

// Public "Leave Feedback" endpoint (no auth required). Callers must pass text
// through sanitizeFeedbackText first so only clean text reaches the backend.
// The token is optional: when the visitor is signed in it lets the server
// attach their account to the entry's metadata.
export const feedbackApi = {
  submit: (text: string, token?: string | null) =>
    request<{ message: string }>('/feedback', {
      method: 'POST',
      body: JSON.stringify({ text }),
      ...(token ? { headers: { ...authHeaders(token), 'Content-Type': 'application/json' } } : {}),
    }),
};

// Public newsletter / "get notified" endpoints (no auth required).
export const subscribeApi = {
  subscribe: (email: string) =>
    request<{ message: string }>('/subscribe', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  unsubscribe: (token: string) =>
    request<{ message: string }>('/subscribe/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
};

function jsonAuthHeaders(token: string): HeadersInit {
  return { ...authHeaders(token), 'Content-Type': 'application/json' };
}

export interface ImportResult {
  created: number;
  updated: number;
  total: number;
}

export const adminApi = {
  getProducts: (token: string) =>
    request<Product[]>('/admin/products', { headers: authHeaders(token) }),

  createProduct: (token: string, data: ProductInput & { id?: string }) =>
    request<Product>('/admin/products', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: jsonAuthHeaders(token),
    }),

  updateProduct: (token: string, id: string, data: ProductInput) =>
    request<Product>(`/admin/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: jsonAuthHeaders(token),
    }),

  archiveProduct: (token: string, id: string) =>
    request<{ message: string; product: Product }>(`/admin/products/${id}/archive`, {
      method: 'POST',
      headers: authHeaders(token),
    }),

  unarchiveProduct: (token: string, id: string) =>
    request<{ message: string; product: Product }>(`/admin/products/${id}/unarchive`, {
      method: 'POST',
      headers: authHeaders(token),
    }),

  async exportProducts(token: string): Promise<string> {
    const res = await fetch(`${BASE}/admin/products/export`, { headers: authHeaders(token) });
    if (!res.ok) throw new Error(`Export failed: ${res.status}`);
    return res.text();
  },

  importProducts: (token: string, csv: string) =>
    request<ImportResult>('/admin/products/import', {
      method: 'POST',
      body: JSON.stringify({ csv }),
      headers: jsonAuthHeaders(token),
    }),

  uploadImage: (token: string, dataUrl: string, filename?: string) =>
    request<{ url: string }>('/admin/uploads', {
      method: 'POST',
      body: JSON.stringify({ dataUrl, filename }),
      headers: jsonAuthHeaders(token),
    }),

  getOrders: (token: string, status?: string) =>
    request<AdminOrder[]>(`/admin/orders${status && status !== 'all' ? `?status=${status}` : ''}`, {
      headers: authHeaders(token),
    }),

  updateOrderStatus: (
    token: string,
    id: string,
    update: { status: string; trackingNumber?: string; trackingCarrier?: string },
  ) =>
    request<Order>(`/admin/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(update),
      headers: jsonAuthHeaders(token),
    }),

  getCustomers: (token: string) =>
    request<CustomerSummary[]>('/admin/customers', { headers: authHeaders(token) }),

  getCustomerDetail: (token: string, email: string) =>
    request<CustomerDetail>(`/admin/customers/detail?email=${encodeURIComponent(email)}`, {
      headers: authHeaders(token),
    }),

  getSubscribers: (token: string) =>
    request<SubscriberSummary[]>('/admin/subscribers', { headers: authHeaders(token) }),

  // One page of feedback, newest first. Pass the previous page's `nextCursor`
  // (S3 continuation token) to keep scrolling further back.
  getFeedback: (token: string, cursor?: string | null) =>
    request<FeedbackListPage>(
      `/admin/feedback${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
      { headers: authHeaders(token) },
    ),

  // Soft delete: moves the S3 file to the archive prefix (kept, not destroyed).
  // The id is the object key (contains slashes), hence body not path param.
  archiveFeedback: (token: string, id: string) =>
    request<{ message: string }>('/admin/feedback/archive', {
      method: 'POST',
      body: JSON.stringify({ id }),
      headers: jsonAuthHeaders(token),
    }),

  getProductBuyers: (token: string, productId: string) =>
    request<{ product: Product; buyers: ProductBuyer[] }>(`/admin/products/${productId}/buyers`, {
      headers: authHeaders(token),
    }),

  getSettings: (token: string) =>
    request<StoreSettings>('/admin/settings', { headers: authHeaders(token) }),

  updateSettings: (token: string, settings: StoreSettings) =>
    request<StoreSettings>('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
      headers: jsonAuthHeaders(token),
    }),
};
